//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { DateUtils } from 'unison';

import * as instagramUserApi from '@apis/instagram-user/instagramUserApi';
import { IInstagramEngager } from '@apis/instagram-user/instagramUserApiModel';
import { SUMMARY_TOP_RECENT_ENGAGERS_LIMIT } from '@instagram-account/components/summary/instagramSummaryConstants';
import IntervalParamHelper from '@instagram-account/instagram-account-constants/summary-stats-interval-helper';
import { INSTAGRAM_AUDIENCE_ACTIONS } from '@instagram-account/redux/audience/instagramAccountAudienceModel';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { createAction } from '@utils/actions';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { ReduxDispatch } from 'app/curalate.store';

const requestSummaryTopRecentEngagers = () => {
  return createAction({
    type: INSTAGRAM_AUDIENCE_ACTIONS.REQUEST_SUMMARY_TOP_RECENT_ENGAGERS,
  });
};

const receiveSummaryTopRecentEngagers = (engagers: IInstagramEngager[]) => {
  return createAction({
    type: INSTAGRAM_AUDIENCE_ACTIONS.RECEIVE_SUMMARY_TOP_RECENT_ENGAGERS,
    engagers,
  });
};

const receiveSummaryTopRecentEngagersFailure = (error: string) => {
  return createAction({
    type: INSTAGRAM_AUDIENCE_ACTIONS.RECEIVE_SUMMARY_TOP_RECENT_ENGAGERS_FAILURE,
    error,
  });
};

export type InstagramAccountAudienceActions = ReturnType<
  | typeof requestSummaryTopRecentEngagers
  | typeof receiveSummaryTopRecentEngagers
  | typeof receiveSummaryTopRecentEngagersFailure
>;

export const instagramAccountAudienceActions = {
  retrieveSummaryTopRecentEngagers:
    (
      dateRange: IUnisonDateRange = DateUtils.getDateRangeFromAlias(
        'last7Days',
      ),
    ) =>
    async (dispatch: ReduxDispatch) => {
      dispatch(requestSummaryTopRecentEngagers());

      try {
        const requestInterval =
          dateRange.startDate &&
          IntervalParamHelper.fromDateRange(dateRange, 0, true);

        const { engagers } =
          await instagramUserApi.getDefaultInstagramTopRecentEngagers({
            interval: requestInterval,
            limit: SUMMARY_TOP_RECENT_ENGAGERS_LIMIT,
          });

        dispatch(receiveSummaryTopRecentEngagers(engagers));
      } catch (e) {
        const error =
          getRequestErrorMessage(e) ??
          'Unknown error retrieving top recent engagers';

        dispatch(receiveSummaryTopRecentEngagersFailure(error));
      }
    },
};
